import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Contact, Quote } from '@lockton-apps/shared/interfaces';
import {
  AssignContactsDialogComponent,
  AssignContactsDialogData,
  AssignContactsDialogResult,
} from './assign-contacts-dialog.component';
import {
  FormDialogCloseResult,
  FormDialogComponent,
  FormDialogData,
} from '@lockton-apps/shared/ui/form-dialog';
import { FormlySelectOption } from '@ngx-formly/core/select';
import { Observable, of } from 'rxjs';
import {
  ConfirmationDialogComponent,
  ConfirmDialogData,
} from '@lockton-apps/shared/ui/lui';
import { InviteRequirementsDialogContentComponent } from './invite-requirements/invite-requirements-dialog-content.component';

@Injectable({
  providedIn: 'root',
})
export class QuotesDialogService {
  constructor(private dialog: MatDialog) {}

  showInviteRequirements(quote: Quote) {
    const hasBroker = !!quote.assignedUserId;
    const hasContact = !!quote.assignedContactUsers.length;
    const hasNeither = !hasBroker && !hasContact;

    if (hasBroker && hasContact) {
      return of(false);
    }

    return this.dialog
      .open<ConfirmationDialogComponent, ConfirmDialogData, boolean>(
        ConfirmationDialogComponent,
        {
          data: {
            title: $localize`Unable to send invite`,
            content: InviteRequirementsDialogContentComponent,
            contentData: {
              inputs: {
                hasBroker,
                hasContact,
              },
            },
            confirmButtonText: hasNeither
              ? $localize`Assign broker and contacts`
              : !hasBroker
              ? $localize`Assign broker`
              : $localize`Assign contact`,
            cancelButtonText: $localize`Cancel`,
            dismissible: true,
          },
        }
      )
      .afterClosed();
  }

  assignContacts(quote: Quote, contacts: Contact[]) {
    return this.dialog
      .open<
        AssignContactsDialogComponent,
        AssignContactsDialogData,
        AssignContactsDialogResult
      >(AssignContactsDialogComponent, {
        data: {
          quote,
          contacts,
        },
      })
      .afterClosed();
  }

  createProposalDocument(
    proposalId: string,
    templateOptions: Observable<FormlySelectOption[]>,
    proposalOptions: Observable<FormlySelectOption[]>
  ) {
    return this.dialog
      .open<FormDialogComponent, FormDialogData, FormDialogCloseResult>(
        FormDialogComponent,
        {
          data: {
            title: $localize`Create Document`,
            fields: [
              {
                fieldGroupClassName: 'vstack gap-1',
                fieldGroup: [
                  {
                    className: 'mb-2',
                    template: $localize`To create your document please choose the relevant template type and the proposal form you wish to include.`,
                  },
                  {
                    key: 'templateId',
                    type: 'select',
                    props: {
                      label: $localize`Template`,
                      options: templateOptions,
                      required: true,
                    },
                  },
                  {
                    key: 'proposalId',
                    type: 'select',
                    defaultValue: proposalId,
                    props: {
                      label: $localize`Choose proposal form`,
                      options: proposalOptions,
                      required: true,
                    },
                  },
                ],
              },
            ],
            mode: 'create',
            model: {},
            confirmLabel: $localize`Create document`,
          },
        }
      )
      .afterClosed();
  }

  updateProposalType(
    quote: Quote,
    proposalOptions: Observable<FormlySelectOption[]>
  ) {
    return this.dialog
      .open<FormDialogComponent, FormDialogData, FormDialogCloseResult>(
        FormDialogComponent,
        {
          data: {
            title: $localize`Update Proposal`,
            fields: [
              {
                fieldGroupClassName: 'vstack gap-1',
                fieldGroup: [
                  {
                    className: 'mb-2',
                    template: $localize`To update your proposal please choose the relevant proposal form you wish to include.`,
                  },
                  {
                    key: 'proposalId',
                    type: 'select',
                    props: {
                      label: $localize`Choose proposal form`,
                      options: proposalOptions,
                      required: true,
                    },
                  },
                ],
              },
            ],
            mode: 'create',
            model: {
              proposalId: `${quote.portalId}-${quote.proposal?.subType}`,
            },
            confirmLabel: $localize`Update Proposal`,
          },
        }
      )
      .afterClosed();
  }
}
